'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { Input } from './input';

export interface CurrencyInputProps {
  /** The numeric value (controlled) */
  value: number | undefined | null;
  /** Callback when the numeric value changes */
  onChange: (value: number) => void;
  /** Callback when the input loses focus */
  onBlur?: () => void;
  /** Callback for keydown events */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Input name attribute */
  name?: string;
  /** Input id (so a `<Label htmlFor>` stays programmatically associated) */
  id?: string;
  /** Marks the field required (a11y `aria-required`) */
  required?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Additional className for styling */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Ref for the input element */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Optional leading currency symbol/code (e.g. "$", "€", "CHF") rendered as an
   * inline adornment so consumers don't re-roll a prefix wrapper per field.
   */
  currencySymbol?: string;
}

/**
 * A controlled currency/amount input that properly handles decimal input.
 *
 * Uses text input with decimal inputMode to avoid browser number input issues
 * (like leading zeros when typing). Maintains an internal display state to
 * allow typing decimals smoothly (e.g., "30." -> "30.5").
 *
 * Returns 0 when the input is empty.
 *
 * @example
 * <CurrencyInput
 *   value={field.value}
 *   onChange={field.onChange}
 *   onBlur={field.onBlur}
 *   name={field.name}
 *   inputRef={field.ref}
 * />
 */
export function CurrencyInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  name,
  id,
  required,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  placeholder = '0.00',
  className,
  disabled,
  inputRef,
  currencySymbol,
}: CurrencyInputProps) {
  // Track the display string separately to allow typing decimals
  const [displayValue, setDisplayValue] = React.useState(() =>
    value != null && value !== 0 ? String(value) : '',
  );

  // Sync display value when form value changes externally (e.g., reset)
  React.useEffect(() => {
    const numericDisplay =
      displayValue === '' ? 0 : parseFloat(displayValue) || 0;
    const effectiveValue = value ?? 0;
    if (numericDisplay !== effectiveValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs internal display string to external numeric form value (e.g., react-hook-form reset)
      setDisplayValue(value != null && value !== 0 ? String(value) : '');
    }
  }, [value, displayValue]);

  const input = (
    <Input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      className={cn(currencySymbol && 'pl-9', className)}
      value={displayValue}
      onChange={(e) => {
        const newValue = e.target.value;
        // Allow empty, numbers, and decimals only
        if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
          setDisplayValue(newValue);
          // Update form value (returns 0 for empty)
          const parsed = newValue === '' ? 0 : parseFloat(newValue) || 0;
          onChange(parsed);
        }
      }}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      name={name}
      id={id}
      required={required}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      disabled={disabled}
      ref={inputRef}
    />
  );

  if (!currencySymbol) {
    return input;
  }

  return (
    <div className="relative">
      <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
        {currencySymbol}
      </span>
      {input}
    </div>
  );
}
